/**
 * The rundown editor page (container, PLAN §6.2, D25): the live session
 * (`api/rundownEditor.ts`) in, the presentational editor out. It owns the
 * page's UI state — focus and selection, collapsed rows, the field being
 * edited, the delete confirmation, the on-air protection — and binds the
 * tree's commands (`lib/editor/commands.ts`) to it, drops to moves
 * (`lib/editor/drop.ts`), and focus to presence (`presence.update` of the
 * focused row and field).
 *
 * **Every undoable edit, undo and redo goes through the on-air guard**
 * (`useOnAirGuard`): while a part of this rundown is on air or next in a
 * production (`useOnAirParts`) and the protection is locked, an edit that
 * changes such a part (`lib/editor/onAirGuard.ts`) waits for the operator's
 * confirmation in `OnAirEditDialog` and is rebuilt when confirmed; "apply and
 * unlock" lifts the protection for `UNLOCK_MS`.
 */
import type { RowPatch } from '@acp/domain/row.js';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { EditorNotice, EditorSession } from '../api/rundownEditor.js';
import { useEditorSnapshot, useNow, useOnAirParts, useRundownEditorSession, useServices, useSession } from '../app/hooks.js';
import { DeleteRowsDialog } from '../components/editor/DeleteRowsDialog.js';
import { EditorToolbar } from '../components/editor/EditorToolbar.js';
import { OnAirEditDialog, OnAirProtection } from '../components/editor/OnAirProtection.js';
import { PresenceAvatars } from '../components/editor/PresenceAvatars.js';
import { ReconnectBanner } from '../components/editor/ReconnectBanner.js';
import { RundownTree } from '../components/editor/RundownTree.js';
import { SaveStatus } from '../components/editor/SaveStatus.js';
import { Button } from '../components/ui/button.js';
import { type Build, type GuardedCommit, namesOf, runCommand, type Ui } from '../lib/editor/commands.js';
import { type DropTarget, planDrop } from '../lib/editor/drop.js';
import { createCommandFor, EMPTY_NAV, focusAfterDelete, type NavState, navRows, pruneNav, type TreeCommand } from '../lib/editor/keyboard.js';
import { decideEdit, type GuardedPart, isLocked, NO_ON_AIR, type OnAirParts, playoutMarks, UNLOCK_MS } from '../lib/editor/onAirGuard.js';
import { deletePatches, movePatches, setPatch } from '../lib/editor/patches.js';
import { EMPTY_PRESENCE, presenceView } from '../lib/editor/presence.js';
import { type FlatRow, flattenTree, type TreeKind } from '../lib/editor/viewModel.js';
import { EditorInspector } from './EditorInspector.js';

function notifyToast(notice: EditorNotice): void {
  if (notice.level === 'error') toast.error(notice.message);
  else toast(notice.message);
}

/** The field a row's inline name edit writes. */
function nameField(kind: TreeKind): 'title' | 'name' {
  return kind === 'part' ? 'title' : 'name';
}

function rowTitle(session: EditorSession, rowId: string, field: 'name' | 'title', fallback: string): string {
  const value = session.store.get(rowId)?.f[field];
  return typeof value === 'string' && value !== '' ? value : fallback;
}

type PendingGuard = { readonly build: Build; readonly parts: readonly GuardedPart[]; readonly after: ((applied: readonly RowPatch[]) => void) | null };

/**
 * The on-air protection: the guarded commit and undo/redo, the edit waiting
 * for confirmation, and the unlock deadline. Whether it is locked is read
 * from the clock when an edit is made (`isLocked`), so nothing here ticks —
 * the toolbar's countdown ticks on its own (`ProtectionControl`). A confirmed
 * edit is rebuilt from the tree as it is then; `onCancel` lets the page reset
 * what the refused edit left in its inputs.
 */
function useOnAirGuard(session: EditorSession, onAir: OnAirParts, onCancel: () => void) {
  const [unlockedUntil, setUnlockedUntil] = useState<number | null>(null);
  const [pending, setPending] = useState<PendingGuard | null>(null);
  const { nowMs } = useServices();
  /** Whether `patches` must ask first; if so, `guard` waits for the answer. */
  const asks = (guard: Omit<PendingGuard, 'parts'>, patches: readonly RowPatch[]): boolean => {
    const decision = decideEdit(session.store.tree, onAir, isLocked(unlockedUntil, nowMs()), patches);
    if (decision.kind === 'ask') setPending({ ...guard, parts: decision.parts });
    return decision.kind === 'ask';
  };
  const commit: GuardedCommit = (build, after) => {
    const patches = build();
    if (patches.length === 0 || asks({ build, after }, patches)) return;
    session.commit(patches, true);
    after?.(patches);
  };
  const step = (direction: 'undo' | 'redo') => {
    const run = () => (direction === 'undo' ? session.undo() : session.redo());
    if (!asks({ build: () => [], after: run }, session.previewStep(direction))) run();
  };
  const confirm = (unlock: boolean) => {
    setPending(null);
    if (unlock) setUnlockedUntil(nowMs() + UNLOCK_MS);
    if (pending === null) return;
    const patches = pending.build();
    if (patches.length > 0) session.commit(patches, true);
    pending.after?.(patches);
  };
  return {
    unlockedUntil,
    pending,
    commit,
    step,
    confirm,
    lock: () => setUnlockedUntil(null),
    unlock: () => setUnlockedUntil(nowMs() + UNLOCK_MS),
    cancel: () => {
      setPending(null);
      onCancel();
    },
  };
}

/** The toolbar's protection toggle and its countdown: the one-second tick re-renders this, not the editor. */
function ProtectionControl({ live, unlockedUntil, onLock, onUnlock }: { readonly live: boolean; readonly unlockedUntil: number | null; readonly onLock: () => void; readonly onUnlock: () => void }) {
  const now = useNow(unlockedUntil === null ? 60_000 : 1_000);
  const locked = isLocked(unlockedUntil, now);
  // `now` can lag the click that unlocked by up to a tick: never show more than an unlock lasts.
  const remainingMs = locked || unlockedUntil === null ? null : Math.min(UNLOCK_MS, unlockedUntil - now);
  return <OnAirProtection live={live} locked={locked} remainingMs={remainingMs} onLock={onLock} onUnlock={onUnlock} />;
}

/** Warn before the tab closes while edits are held (the offline queue lives in memory). */
function useUnloadWarning(pending: number): void {
  useEffect(() => {
    if (pending === 0) return undefined;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pending]);
}

/**
 * The live region's text. A repeated message ("Cannot move further.") must
 * change the DOM to be read again, so every other one carries a trailing
 * no-break space.
 */
function useAnnouncer(): readonly [string, (message: string) => void] {
  const [said, setSaid] = useState({ text: '', count: 0 });
  const announce = (message: string) => setSaid((prev) => ({ text: message, count: prev.count + 1 }));
  return [said.count % 2 === 0 ? said.text : `${said.text} `, announce];
}

/** What the page shows about the focused row: its flat row, the row itself (the inspector) and its keyboard row. */
function focusOf(session: EditorSession, rows: readonly FlatRow[], nav: NavState) {
  const flat = rows.find((r) => r.id === nav.focusedId) ?? null;
  const row = flat === null ? null : session.store.get(flat.id);
  return { flat, inspected: flat === null || row === null ? null : { row, view: flat.view } };
}

export function RundownEditorView({ rundownId }: { readonly rundownId: string }) {
  const session = useRundownEditorSession(rundownId, notifyToast);
  const snap = useEditorSnapshot(session);
  const self = useSession();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [rawNav, setNav] = useState<NavState>(EMPTY_NAV);
  const [field, setField] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<readonly string[] | null>(null);
  const [announcement, announce] = useAnnouncer();
  /** Bumped when a protected edit is cancelled: the inspector's inputs start over from the rows. */
  const [inspectorReset, setInspectorReset] = useState(0);
  const onAir = useOnAirParts(rundownId, session.store.get(rundownId)?.parentId ?? null).data ?? NO_ON_AIR;
  const guard = useOnAirGuard(session, onAir, () => setInspectorReset((n) => n + 1));
  useUnloadWarning(snap.pending);

  const flat = useMemo(() => flattenTree(snap.rows.tree, rundownId, collapsed), [snap.rows, rundownId, collapsed]);
  const rowsNav = useMemo(() => navRows(flat.rows), [flat]);
  const marks = useMemo(() => playoutMarks(onAir), [onAir]);
  const nav = pruneNav(rawNav, rowsNav);
  const presence = useMemo(() => (self === null ? EMPTY_PRESENCE : presenceView(snap.members, self.userId)), [snap.members, self]);
  const displayOrder = useMemo(() => flat.rows.map((r) => r.id), [flat]);
  const focus = focusOf(session, flat.rows, nav);
  const focusedNav = rowsNav.find((r) => r.id === nav.focusedId) ?? null;

  // Presence follows focus: the row, and the field being edited (inline name, inspector field, script).
  const editingField = nav.editingId !== null && focus.flat !== null ? nameField(focus.flat.view.kind) : field;
  useEffect(() => {
    session.setPresence({ rowId: nav.focusedId, field: nav.focusedId === null ? null : editingField, activity: editingField === null ? 'viewing' : 'editing' });
  }, [session, nav.focusedId, editingField]);

  if (snap.status === 'failed') {
    return (
      <div className="mx-auto grid max-w-xl gap-4 p-6" role="alert" data-testid="editor-failed">
        <p className="text-sm text-destructive">Could not open the rundown: {snap.error}</p>
        <Button variant="outline" onClick={() => session.retry()} className="w-fit">
          Retry
        </Button>
      </div>
    );
  }

  const ui: Ui = { updateCollapsed: setCollapsed, setNav, setConfirmDelete, announce, commit: guard.commit, step: guard.step, displayOrder };
  const onCommand = (command: TreeCommand) => runCommand(session, rundownId, ui, command);
  const createFor = (kind: TreeKind) => createCommandFor(kind, focusedNav, rowsNav);
  const planFor = (ids: readonly string[], target: DropTarget) => planDrop(session.store.tree, displayOrder, ids, target, session.writer.ordTag);
  const totalDuration = flat.rows.reduce((sum, r) => (r.view.kind === 'segment' && !r.lost ? sum + r.view.totalDuration : sum), 0);
  const ready = snap.status === 'ready';

  return (
    <div className="flex h-full flex-col" data-testid="rundown-editor">
      <EditorToolbar
        title={ready ? rowTitle(session, rundownId, 'name', '(unnamed rundown)') : 'Loading…'}
        totalDuration={totalDuration}
        canCreate={{ segment: ready, part: createFor('part') !== null, piece: createFor('piece') !== null, adlib: ready }}
        canUndo={snap.canUndo}
        canRedo={snap.canRedo}
        canDelete={nav.selected.length > 0}
        onCreate={(kind) => {
          const command = createFor(kind);
          if (command !== null) onCommand(command);
        }}
        onUndo={() => guard.step('undo')}
        onRedo={() => guard.step('redo')}
        onDelete={() => setConfirmDelete(displayOrder.filter((id) => nav.selected.includes(id)))}
        status={
          <>
            <ProtectionControl live={marks.size > 0} unlockedUntil={guard.unlockedUntil} onLock={guard.lock} onUnlock={guard.unlock} />
            {/* Offline as soon as the live socket is lost, not only once a send has given up (the failover client waits for a primary first). */}
            <SaveStatus pending={snap.pending} offline={snap.offline || snap.connection === 'reconnecting'} />
          </>
        }
        presence={<PresenceAvatars users={presence.users} />}
      />
      <ReconnectBanner connection={snap.status === 'loading' ? 'connecting' : snap.connection} retryInMs={snap.retryInMs} pending={snap.pending} />
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1">
          <RundownTree
            rows={flat.rows}
            presence={presence.byRow}
            nav={nav}
            onNavChange={setNav}
            onCommand={onCommand}
            onRename={(id, value) => {
              const row = flat.rows.find((r) => r.id === id);
              if (row !== undefined) guard.commit(() => [setPatch(session.writer, id, { [nameField(row.view.kind)]: value })], null);
            }}
            canDrop={(ids, target) => planFor(ids, target) !== null}
            onDrop={(ids, target) =>
              guard.commit(() => {
                const moves = planFor(ids, target);
                return moves === null ? [] : movePatches(session.writer, moves);
              }, null)
            }
            playout={marks}
            announcement={announcement}
          />
        </div>
        <div className="w-96 shrink-0 overflow-auto border-l bg-card">
          <EditorInspector
            key={inspectorReset}
            session={session}
            commit={(build) => guard.commit(build, null)}
            step={guard.step}
            focused={focus.inspected}
            presence={nav.focusedId === null ? [] : (presence.byRow.get(nav.focusedId) ?? [])}
            onFocusField={setField}
          />
        </div>
      </div>
      <DeleteRowsDialog
        open={confirmDelete !== null}
        names={namesOf(flat.rows, confirmDelete ?? [])}
        onOpenChange={(open) => {
          if (!open) setConfirmDelete(null);
        }}
        onConfirm={() => {
          const ids = confirmDelete;
          setConfirmDelete(null);
          if (ids !== null) guard.commit(() => deletePatches(session.writer, ids), () => setNav(focusAfterDelete(rowsNav, ids)));
        }}
      />
      <OnAirEditDialog
        open={guard.pending !== null}
        parts={(guard.pending?.parts ?? []).map((p) => ({ name: rowTitle(session, p.partId, 'title', '(untitled part)'), mark: p.mark }))}
        unlockMs={UNLOCK_MS}
        onApply={() => guard.confirm(false)}
        onApplyAndUnlock={() => guard.confirm(true)}
        onCancel={guard.cancel}
      />
    </div>
  );
}
